import { useCallback, useEffect, useRef, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import {
  FundingDataProps,
  FundingRequest,
  FundingRevenueSummary,
  FundingTodo,
  FundingWalletProgram,
} from '../../types'
import {
  FundingActivity,
  FundingBenefitsAccessReason,
  FundingEligibilityProgram,
  FundingRequestsQuery,
  FundingWalletItem,
  RecommendationGroupSummary,
  getFundingActivities,
  getFundingBenefitsAccess,
  getFundingEligibilityPrograms,
  getFundingRequests,
  getFundingSummary,
  getFundingWallet,
} from '../../api/fundingApi'
import { formatWalletDate } from '../../utils/format'
import { FundingIamDeniedCta } from '../FundingBenefitsSetupPage/FundingBenefitsSetupPage'
import { RevenueSummary } from '../RevenueSummary/RevenueSummary'
import { FundingRequests } from '../FundingRequests/FundingRequests'
import { FundingWallet } from '../FundingWallet/FundingWallet'
import { FundingToDos } from '../FundingToDos/FundingToDos'
import { RecentFundingActivities } from '../RecentFundingActivities/RecentFundingActivities'
import { useStyles } from './FundingDashboard.styles'

const REQUESTS_PAGE_SIZE = 20
const ACTIVITIES_PAGE_SIZE = 20

const toAmount = (value: string | null | undefined): number =>
  value == null ? 0 : Number(value) || 0

/** Map the wallet API item to the presentational shape the panel renders.
 * `funding_needed_amount` drives both the "Extra required" bar segment and the
 * shortfall warning (the service exposes a single shortfall figure). */
const toWalletProgram = (w: FundingWalletItem): FundingWalletProgram => {
  const needed = toAmount(w.funding_needed_amount)
  return {
    id: w.labra_identifier,
    name: w.wallet_name ?? `AWS ${w.program}`,
    lastUpdated: formatWalletDate(w.last_refreshed_at),
    currency: w.currency,
    total: toAmount(w.allocated_amount),
    consumed: toAmount(w.consumed_amount),
    available: toAmount(w.remaining_amount),
    extraRequired: needed,
    shortfall: needed,
  }
}

const toTodo = (g: RecommendationGroupSummary): FundingTodo | null => {
  const action = g.action?.trim()
  const headline =
    g.sample?.[0]?.customer_name?.trim() ||
    g.sample?.[0]?.opportunity_id?.trim()
  if (!action || !headline) return null
  const others = g.count - 1
  const suffix =
    others <= 0
      ? '.'
      : others === 1
        ? ' and 1 other.'
        : ` and ${others} others.`
  return {
    id: g.group_id,
    text: `${action} for ${headline}${suffix}`,
    source: g.source,
  }
}

export const FundingDashboard = ({
  accountId,
  serviceUrl,
  isFundingSettingsEnabled = false,
}: FundingDataProps) => {
  const { classes } = useStyles()
  const navigate = useNavigate()
  const [revenue, setRevenue] = useState<FundingRevenueSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [requests, setRequests] = useState<FundingRequest[]>([])
  const [requestsTotal, setRequestsTotal] = useState(0)
  const [requestsLoading, setRequestsLoading] = useState(true)
  const [query, setQuery] = useState<FundingRequestsQuery>({})
  const [wallets, setWallets] = useState<FundingWalletProgram[]>([])
  const [walletsLoading, setWalletsLoading] = useState(true)
  const [todos, setTodos] = useState<FundingTodo[]>([])
  const [activities, setActivities] = useState<FundingActivity[]>([])
  const [activitiesLoading, setActivitiesLoading] = useState(true)
  const [programs, setPrograms] = useState<FundingEligibilityProgram[]>([])
  const [programsLoading, setProgramsLoading] = useState(true)
  const [benefitsAccessReason, setBenefitsAccessReason] = useState<
    FundingBenefitsAccessReason | null
  >(null)
  const [benefitsAccessReady, setBenefitsAccessReady] = useState(false)
  const fetchingMoreRef = useRef(false)

  const applyBenefitsAccess = useCallback(
    (reason: FundingBenefitsAccessReason | null) => {
      setBenefitsAccessReason(reason)
      setBenefitsAccessReady(true)
    },
    []
  )

  useEffect(() => {
    if (!serviceUrl || !accountId) return
    let ignore = false
    setBenefitsAccessReady(false)

    const probe = async (refresh: boolean) => {
      try {
        const { data } = await getFundingBenefitsAccess(
          serviceUrl,
          accountId,
          refresh ? { refresh: true } : undefined
        )
        if (!ignore) applyBenefitsAccess(data.reason ?? null)
      } catch {
        // Fail open so the dashboard stays usable if the probe is unavailable.
        if (!ignore) applyBenefitsAccess('ok')
      }
    }

    void probe(false)

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void probe(true)
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      ignore = true
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [serviceUrl, accountId, applyBenefitsAccess])

  const showFeatureSetupBanner =
    benefitsAccessReason === 'feature_not_enabled'
  const showIamBanner =
    benefitsAccessReason === 'iam_denied' ||
    benefitsAccessReason === 'role_missing'
  const hideDashboardContent =
    !benefitsAccessReady || showFeatureSetupBanner || showIamBanner
  const canLoadDashboardData = benefitsAccessReady && !hideDashboardContent

  useEffect(() => {
    if (!canLoadDashboardData || !serviceUrl || !accountId) {
      if (!canLoadDashboardData) setLoading(false)
      return
    }
    let ignore = false
    setLoading(true)
    ;(async () => {
      try {
        const { data } = await getFundingSummary(serviceUrl, accountId)
        if (ignore) return
        const c = data.combined
        setRevenue({
          currency: c.currency,
          approved: { count: c.approved_count, amount: toAmount(c.approved) },
          pending: { count: c.pending_count, amount: toAmount(c.pending) },
          potential: {
            count: c.potential_count ?? 0,
            amount: toAmount(c.potential),
          },
        })
        setTodos(
          (c.recommendation_groups ?? [])
            .map(toTodo)
            .filter((t): t is FundingTodo => t !== null)
        )
      } catch {
        if (!ignore) {
          setRevenue(null)
          setTodos([])
        }
      } finally {
        if (!ignore) setLoading(false)
      }
    })()
    return () => {
      ignore = true
    }
  }, [serviceUrl, accountId, canLoadDashboardData])

  useEffect(() => {
    if (!canLoadDashboardData || !serviceUrl || !accountId) {
      if (!canLoadDashboardData) setRequestsLoading(false)
      return
    }
    let ignore = false
    setRequestsLoading(true)
    ;(async () => {
      try {
        const { data } = await getFundingRequests(serviceUrl, accountId, {
          ...query,
          offset: 0,
          limit: REQUESTS_PAGE_SIZE,
        })
        if (ignore) return
        setRequests(data.requests ?? [])
        setRequestsTotal(data.total ?? 0)
      } catch {
        if (!ignore) {
          setRequests([])
          setRequestsTotal(0)
        }
      } finally {
        if (!ignore) setRequestsLoading(false)
      }
    })()
    return () => {
      ignore = true
    }
  }, [serviceUrl, accountId, query, canLoadDashboardData])

  useEffect(() => {
    if (!canLoadDashboardData || !serviceUrl || !accountId) {
      if (!canLoadDashboardData) setWalletsLoading(false)
      return
    }
    // `ignore` drops a stale response: if accountId changes (or we unmount)
    // mid-flight, the late resolve must not overwrite the newer state.
    let ignore = false
    setWalletsLoading(true)
    ;(async () => {
      try {
        const { data } = await getFundingWallet(serviceUrl, accountId)
        if (!ignore) setWallets((data.wallets ?? []).map(toWalletProgram))
      } catch {
        if (!ignore) setWallets([])
      } finally {
        if (!ignore) setWalletsLoading(false)
      }
    })()
    return () => {
      ignore = true
    }
  }, [serviceUrl, accountId, canLoadDashboardData])

  useEffect(() => {
    if (!canLoadDashboardData || !serviceUrl || !accountId) {
      if (!canLoadDashboardData) setActivitiesLoading(false)
      return
    }
    let ignore = false
    setActivitiesLoading(true)
    ;(async () => {
      try {
        const { data } = await getFundingActivities(serviceUrl, accountId, {
          offset: 0,
          limit: ACTIVITIES_PAGE_SIZE,
        })
        if (!ignore) setActivities(data.activities ?? [])
      } catch {
        if (!ignore) setActivities([])
      } finally {
        if (!ignore) setActivitiesLoading(false)
      }
    })()
    return () => {
      ignore = true
    }
  }, [serviceUrl, accountId, canLoadDashboardData])

  useEffect(() => {
    if (!canLoadDashboardData || !serviceUrl || !accountId) {
      if (!canLoadDashboardData) setProgramsLoading(false)
      return
    }
    let ignore = false
    setProgramsLoading(true)
    ;(async () => {
      try {
        const { data } = await getFundingEligibilityPrograms(
          serviceUrl,
          accountId
        )
        if (!ignore) setPrograms(data.programs ?? [])
      } catch {
        if (!ignore) setPrograms([])
      } finally {
        if (!ignore) setProgramsLoading(false)
      }
    })()
    return () => {
      ignore = true
    }
  }, [serviceUrl, accountId, canLoadDashboardData])

  const requestPath = (
    request: Pick<FundingRequest, 'program' | 'workflow_id'>
  ) => `requests/${encodeURIComponent(request.program)}/${request.workflow_id}`

  const openRequest = useCallback(
    (request: Pick<FundingRequest, 'program' | 'workflow_id'>) =>
      navigate(requestPath(request)),
    [navigate]
  )

  const hasMoreRequests = requests.length < requestsTotal

  const fetchMoreRequests = useCallback(async () => {
    if (!serviceUrl || !accountId) return
    if (fetchingMoreRef.current) return
    if (requests.length >= requestsTotal) return
    fetchingMoreRef.current = true
    try {
      const { data } = await getFundingRequests(serviceUrl, accountId, {
        ...query,
        offset: requests.length,
        limit: REQUESTS_PAGE_SIZE,
      })
      setRequests(prev => [...prev, ...(data.requests ?? [])])
      if (typeof data.total === 'number') setRequestsTotal(data.total)
    } catch {
      // keep the already-loaded page; the next scroll retries
    } finally {
      fetchingMoreRef.current = false
    }
  }, [serviceUrl, accountId, requests.length, requestsTotal, query])

  return (
    <div className={classes.page} data-testid="funding-dashboard">
      {showFeatureSetupBanner ? (
        <div
          className={classes.setupBanner}
          role="status"
          data-testid="funding-benefits-setup-banner"
        >
          Complete setup in{' '}
          {isFundingSettingsEnabled ? (
            <RouterLink
              className={classes.setupBannerLink}
              to="/settings/funding"
            >
              Settings
            </RouterLink>
          ) : (
            'Settings'
          )}{' '}
          to start using the Funding management features.
        </div>
      ) : null}
      {showIamBanner ? (
        <div
          className={classes.setupBanner}
          role="status"
          data-testid="funding-benefits-iam-banner"
        >
          <FundingIamDeniedCta compact />
        </div>
      ) : null}
      {!hideDashboardContent ? (
        <div className={classes.root} data-testid="funding-dashboard-content">
          <div className={classes.leftColumn}>
            <RevenueSummary revenue={revenue} loading={loading} />
            <FundingRequests
              requests={requests}
              total={requestsTotal}
              loading={requestsLoading}
              hasMore={hasMoreRequests}
              fetchMore={fetchMoreRequests}
              currency={revenue?.currency ?? 'USD'}
              programs={programs}
              programsLoading={programsLoading}
              accountId={accountId}
              onQueryChange={setQuery}
              onRowClick={openRequest}
              onOpenSettings={
                isFundingSettingsEnabled
                  ? () => navigate('/settings/funding')
                  : undefined
              }
            />
          </div>
          <div className={classes.rightColumn}>
            <FundingToDos todos={todos} loading={loading} />
            <FundingWallet
              programs={wallets}
              loading={walletsLoading}
              serviceUrl={serviceUrl}
              partnerId={accountId}
            />
            <RecentFundingActivities
              activities={activities}
              loading={activitiesLoading}
              requestPath={requestPath}
            />
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default FundingDashboard
